import { ScrollArea } from "@mantine/core";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { LibraryBig, Sparkles } from "lucide-react";
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { getListJobsQueryKey } from "../../api/generated/endpoints/generation/generation";
import type { TemplateCard as Template } from "../../api/generated/model";

import { PageColumns } from "../../ui/PageColumns";
import { TypeTabs } from "../../ui/TypeTabs";
import { TemplateGenerations } from "./TemplateGenerations";
import { TemplatePanel } from "./TemplatePanel";
import { TemplatesLibrary } from "./TemplatesLibrary";
import classes from "./templates.module.css";

const ROUTE = "/signed-in/teams/$teamId/brands/$brandId/templates";

/**
 * Live's Templates page (`/app/templates`, captures `design-ref/gaps/` 01 to 04 and 43, spec
 * G14-templates): the Library of live's cards and My Generations, the brand's runs, the tab in
 * `?tab=` as live's. The template chosen with Use or Replicate keeps its form panel open at the
 * page's foot, over both tabs, until its x; choosing it the other way starts it again.
 */
export function TemplatesScreen() {
  const { teamId, brandId } = useParams({ from: ROUTE });
  const search = useSearch({ from: ROUTE });
  const tab = search.tab ?? "library";
  const navigate = useNavigate({ from: "/teams/$teamId/brands/$brandId/templates" });
  const queryClient = useQueryClient();
  const show = (next: string) =>
    void navigate({ search: { tab: next === "my-generations" ? next : "library" }, replace: true });
  const [selected, setSelected] = useState<{ template: Template; replicate: boolean } | null>(
    null,
  );
  return (
    <PageColumns
      scrollable={false}
      bottom={
        selected === null ? null : (
          <TemplatePanel
            key={`${selected.template.id}:${String(selected.replicate)}`}
            teamId={teamId}
            brandId={brandId}
            template={selected.template}
            replicate={selected.replicate}
            onClose={() => setSelected(null)}
            onStarted={() => {
              void queryClient.invalidateQueries({ queryKey: getListJobsQueryKey(teamId) });
              // Live shows My Generations once a run starts; the panel stays for another.
              show("my-generations");
            }}
          />
        )
      }
      top={
        <TypeTabs
          label="Templates"
          value={tab}
          onChange={show}
          tabs={[
            { value: "library", label: "Library", icon: <LibraryBig size={16} /> },
            { value: "my-generations", label: "My Generations", icon: <Sparkles size={16} /> },
          ]}
        />
      }
    >
      {tab === "my-generations" ? (
        // Its own scroll under the tabs, as live's tab (issue 784): the page itself does not scroll.
        <ScrollArea type="auto" offsetScrollbars className={classes.fill}>
          <TemplateGenerations teamId={teamId} brandId={brandId} />
        </ScrollArea>
      ) : (
        <TemplatesLibrary
          teamId={teamId}
          onSelect={(template, replicate) => setSelected({ template, replicate })}
          linked={search.template}
          // The link's preview closed: the link goes, so the page is as it was.
          onLinkDone={() => void navigate({ search: { tab: search.tab }, replace: true })}
        />
      )}
    </PageColumns>
  );
}
